import { useEffect, useRef, useState } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { ClinicalFieldRecap } from './ClinicalFieldRecap';
import type { ClinicalFormFields } from './ClinicalFormStep';
import type { ElasticScheme, LegacyElastics } from '@odo/shared-ui/clinical/elastics/model';
import type { TiptapDoc } from '@odo/shared-ui';
import type { ManufacturerPdfRef } from '@odo/shared-ui/clinical/types';
import type { VirtualAppointmentDto } from '../../../../services/virtualAppointments';
import type { VirtualAppointmentDraft } from '../../../virtual-appointments/virtualAppointmentDraft';
import type { VirtualAppointmentLoadStatus } from '@odo/shared-ui/clinical/virtualAppointments/types';
import { useVirtualAppointmentSummaries } from '../../../virtual-appointments/useVirtualAppointmentSummaries';
import { useVirtualAppointmentTypeLabels } from '../../../../hooks/useVirtualAppointments';
import type { SavedTriggers } from '../../../virtual-appointments/virtualAppointmentTriggerAhead';

interface ReviewVirtualAppointments {
    frozen: VirtualAppointmentDto[];
    planned: VirtualAppointmentDraft[];
    savedTriggers: SavedTriggers;
    status: VirtualAppointmentLoadStatus;
    onRetry: () => void;
}

interface ClinicalReviewStepProps {
    patientId: string;
    kindLabel: string;
    fields: ClinicalFormFields;
    elasticsSchemes: ElasticScheme[];
    legacyElastics?: LegacyElastics;
    nextAppointment: string;
    notesDoc: TiptapDoc;
    attachment: File | null;
    attachmentRef: ManufacturerPdfRef | null;
    virtualAppointments: ReviewVirtualAppointments;
    rebuildKey: number;
    onMergedPdfReady: (blob: Blob) => void;
    buildPdf: (attachmentBytes: Uint8Array | null) => Promise<Blob>;
}

type ReviewState =
    | { kind: 'idle' }
    | { kind: 'building' }
    | { kind: 'ready'; url: string; blob: Blob }
    | { kind: 'error'; message: string };

function ClinicalReviewStep({
    patientId,
    kindLabel,
    fields,
    elasticsSchemes,
    legacyElastics,
    nextAppointment,
    notesDoc,
    attachment,
    attachmentRef,
    virtualAppointments,
    rebuildKey,
    onMergedPdfReady,
    buildPdf
}: ClinicalReviewStepProps) {
    const [state, setState] = useState<ReviewState>({ kind: 'idle' });
    const vaTypeLabels = useVirtualAppointmentTypeLabels();
    const vaSummaries = useVirtualAppointmentSummaries(
        virtualAppointments.frozen,
        virtualAppointments.planned,
        vaTypeLabels,
        virtualAppointments.savedTriggers
    );
    const activeUrlRef = useRef<string | null>(null);

    // Cache fetched attachment bytes so re-entering review doesn't refetch.
    const fetchedBytesRef = useRef<Uint8Array | null>(null);

    useEffect(() => {
        let cancelled = false;
        setState({ kind: 'building' });

        async function run() {
            let attachmentBytes: Uint8Array | null = null;

            // When resuming (no raw File) but a persisted ref exists, fetch bytes once.
            if (attachment === null && attachmentRef !== null) {
                if (fetchedBytesRef.current === null) {
                    const { fetchCloudFileDownloadUrl } =
                        await import('../../../../services/patientFiles');
                    const signedUrl = await fetchCloudFileDownloadUrl(
                        patientId,
                        attachmentRef.patientFileId
                    );
                    const res = await fetch(signedUrl);
                    if (!res.ok)
                        throw new Error(`Failed to fetch attachment PDF (status ${res.status})`);
                    const buf = await res.arrayBuffer();
                    fetchedBytesRef.current = new Uint8Array(buf);
                }
                attachmentBytes = fetchedBytesRef.current;
            }

            const blob = await buildPdf(attachmentBytes);
            if (cancelled) return;
            const url = URL.createObjectURL(blob);
            if (activeUrlRef.current) URL.revokeObjectURL(activeUrlRef.current);
            activeUrlRef.current = url;
            setState({ kind: 'ready', url, blob });
            onMergedPdfReady(blob);
        }

        run().catch((error: unknown) => {
            if (cancelled) return;
            const message = error instanceof Error ? error.message : 'Unknown error';
            setState({ kind: 'error', message: `Failed to build PDF: ${message}` });
        });

        return () => {
            cancelled = true;
        };
        // rebuildKey is the explicit signal that upstream data changed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rebuildKey]);

    useEffect(() => {
        return () => {
            if (activeUrlRef.current) {
                URL.revokeObjectURL(activeUrlRef.current);
                activeUrlRef.current = null;
            }
        };
    }, []);

    return (
        <div className="flex flex-col gap-4 h-full min-h-0">
            <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Review &amp; submit</h3>
                <p className="text-xs text-gray-500">
                    Check the merged PDF and the recap below. Use Back to edit any step.
                </p>
            </div>

            <div className="flex-1 min-h-0 grid grid-cols-3 gap-4">
                <div className="col-span-2 min-h-0 rounded-xl border border-gray-200 bg-white overflow-hidden">
                    {state.kind === 'building' && (
                        <div className="flex items-center justify-center h-full">
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-6 h-6 rounded-full border-2 border-gray-300 border-t-gray-800 animate-spin" />
                                <div className="text-xs text-gray-500">Building merged PDF…</div>
                            </div>
                        </div>
                    )}
                    {state.kind === 'error' && (
                        <div className="flex items-center justify-center h-full p-4">
                            <div className="flex flex-col items-center gap-2 text-center">
                                <ExclamationCircleIcon className="w-6 h-6 text-red-500" />
                                <div className="text-xs text-red-500">{state.message}</div>
                            </div>
                        </div>
                    )}
                    {state.kind === 'ready' && (
                        <iframe
                            src={state.url}
                            title={`Merged ${kindLabel} PDF`}
                            className="w-full h-full"
                            style={{ border: 'none' }}
                        />
                    )}
                </div>

                <div className="col-span-1 min-h-0 overflow-y-auto">
                    <ClinicalFieldRecap
                        fields={fields}
                        elasticsSchemes={elasticsSchemes}
                        legacyElastics={legacyElastics}
                        nextAppointment={nextAppointment}
                        notesDoc={notesDoc}
                        virtualAppointments={{
                            summaries: vaSummaries,
                            status: virtualAppointments.status,
                            onRetry: virtualAppointments.onRetry
                        }}
                        attachment={attachment}
                        attachmentRef={attachmentRef}
                    />
                </div>
            </div>
        </div>
    );
}

export { ClinicalReviewStep };
